import Helmet from 'react-helmet';
import React from 'react';
import { withRouter } from 'react-router';
import { connect } from 'react-redux';
import { useDropzone } from 'react-dropzone';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { Button } from 'react-bootstrap';
import Autocomplete from 'react-autocomplete';
import _ from 'lodash';

import { hasDashboardAccess } from '../../util/roles';

import FormattedMessage from '../../components/FormattedMessage';
import Header from '../../components/Header/Header';
import Loading from '../../components/Loading/Loading';
import Switch from '../../components/Switch/Switch';
import Checkbox from '../../components/Checkbox/Checkbox';
import Confirm from '../../components/Confirm/Confirm';

import addIcon from '../../assets/img/add-circle.svg';
import saveIcon from '../../assets/img/save.svg';
import spinerArrow from '../../assets/images/spiner-arrow.svg';

import { PAGES, trackPageView, trackHowToOpened } from '../../util/mixpanel';

import { setSuccessMessage } from '../App/ErrorsActions';
import {
  handleError,
  listAll,
  getLists,
  create,
  updateModel,
  update,
  uploadFile,
  isLoading,
  updateDashboardName
} from '../../components/DashboardSelector/DashboardSelectorActions';
import { setPageName, isLoading as appDataIsLoading } from '../App/AppActions';

import deleteIcon from '../../assets/img/delete.svg';

// Import Style
import './createDashboard.scss';

// a little function to help us with reordering the result
const reorder = (list, startIndex, endIndex) => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);

  return result;
};

/**
 * Moves an item from one list to another list.
 */
const move = (source, destination, droppableSource, droppableDestination) => {
  const sourceClone = Array.from(source);
  const destClone = Array.from(destination);
  const [removed] = sourceClone.splice(droppableSource.index, 1);

  destClone.splice(droppableDestination.index, 0, removed);

  const result = {};
  result[droppableSource.droppableId] = sourceClone;
  result[droppableDestination.droppableId] = destClone;

  return result;
};

const grid = 8;
const getItemStyle = (isDragging, draggableStyle) => ({
  position: 'relative',
  userSelect: 'none',
  minHeight: '40px',
  padding: '8px 40px 8px 8px' ,
  borderRadius: '8px',
  background: '#fff',
  color: '#001A33',
  fontSize: '14px',
  fontWeight: '500',
  lineHeight: '24px',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  ...draggableStyle
});

const getListStyle = isDraggingOver => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  maxHeight: '100%',
  minHeight: '60px',
  padding: '16px',
  borderRadius: '10px',
  background: isDraggingOver ? '#f2f9fb' : '#EFF6FB',
  overflow: 'hidden auto',
});

function DropZone(props) {
  const { getRootProps, getInputProps, isDragActive, isDragAccept, isDragReject } = useDropzone({
    multiple: false,
    accept: ['.json', '.csv', 'application/json', 'text/csv'],
    onDrop: props.onDrop
  });

  return (
    <div className='dropzone_container'>
      <section
        className={`dropzone ${isDragAccept ? 'valid' : ''} ${isDragReject ? 'invalid' : ''}`}
        {...getRootProps()}
      >
        <input {...getInputProps()} />
        {isDragAccept && <p>File accepted</p>}
        {isDragReject && <p>File rejected</p>}
        {!isDragActive && <p>Drop your file here ...</p>}
      </section>
    </div>
  );
}

const getListsByIds = (lists, ids) => {
  return _(lists)
    .remove(obj => ids.includes(obj._id))
    .sortBy(l => {
      return ids.indexOf(l._id);
    })
    .value();
}

export const prepareDashboardModelForState = (dashboardModel, lists, fields) => {
  return {
    name: dashboardModel.name,
    boardId: dashboardModel.boardId,
    dashboardId: dashboardModel._id,
    todo: getListsByIds(lists, dashboardModel.todo),
    wip: getListsByIds(lists, dashboardModel.wip),
    done: getListsByIds(lists, dashboardModel.done),
    fields: fields,
    listsLoaded: true,
    lists: lists,
    isAzure: !!dashboardModel.isAzure,
    isCsv: !!dashboardModel.isCsv
  }
}

class CreateFileDashboard extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      name: '',
      boards: [],
      model: {},
      lists: [],
      todo: [],
      wip: [],
      done: [],
      fieldSearchVal: '',
      selectedFields: [],
      listsLoaded: false,
      dashboardId: '',
      boardId: '',
      cumulative: false,
      isCsv: false,
      wipeOut: false,
      maxSize: 32 * 1024 * 1024
    };

    this.handleCreate = this.handleCreate.bind(this);
    this.handleUpdate = this.handleUpdate.bind(this);
    this.handleCheckboxChange = this.handleCheckboxChange.bind(this);
    this.onDragEnd = this.onDragEnd.bind(this);

    props.setDataLoading(false);
    props.dispatch(setPageName('Create dashboard'));
  }

  componentDidMount() {
    document.addEventListener('click', this.handleOutsideClick, false);

    if (this.props.route.path !== 'create') {
      this.props.dispatch(setPageName('Update dashboard'));
      if (this.props.dashboardModel) {
        this.props.getLists(this.props.dashboardModel.boardId, this.props.dashboardModel.isAzure).catch(() => {
          this.props.router.goBack();
        });
      } else {
        this.props.listAll();
      }
    } else {
      this.props.resetModel();
      this.input && this.input.focus();
    }

    trackPageView(PAGES.CREATE);
  }

  componentWillUnmount() {
    document.removeEventListener('click', this.handleOutsideClick, false);
  }

  componentDidUpdate(prevProps, prevState) {
    if (this.props.route.path === 'create' && prevProps.user != this.props.user) {
      if (
        this.props.user.allowedDashboards > 0 &&
        this.props.user.dashboards > 0 &&
        this.props.user.dashboards >= this.props.user.allowedDashboards
      ) {
        this.props.router.replace('/limitreached');
        return;
      }
    }
    if (this.props.created === true && prevProps.created != this.props.created) {
      this.props.listAll().then(() => {
        this.props.loadingDone();
        this.props.setDataLoading(true);

        const last = _(this.props.dashboards)
          .orderBy('createdAt')
          .last();
        const dashboardId = this.props.params.id ? this.props.params.id : last._id;
        this.props.router.push(`/dashboard/${dashboardId}/process`);
      });
    }

    if (prevProps.dashboards != this.props.dashboards) {
      const dashboard = this.props.dashboards.find(d => d._id == this.props.params.id);
      this.props.updateModel(dashboard);
    }

    if (this.props.dashboardModel && !prevProps.dashboardModel) {
      this.props.getLists(this.props.dashboardModel.boardId, this.props.dashboardModel.isAzure).catch(() => {
        this.props.router.goBack();
      });
    }

    if (prevProps.fields !== this.props.fields) {
      this.setState({
        fields: this.props.fields,
        selectedFields: this.getSelectedFields(this.props.fields, this.props.dashboardModel.selectedFields)
      });
    }

    if (prevState.selectedFields !== this.state.selectedFields) {
      this.setState({
        fieldsValue:
          this.state.selectedFields.length > 0
            ? `${this.state.selectedFields.length} field${this.state.selectedFields.length !== 1 ? 's' : ''} selected`
            : '',
        fieldSearchVal:
          this.state.selectedFields.length > 0
            ? `${this.state.selectedFields.length} field${this.state.selectedFields.length !== 1 ? 's' : ''} selected`
            : ''
      });
    }
  }

  componentWillReceiveProps(newProps) {
    if (newProps.lists && newProps.lists != this.props.lists) {
      this.updateDashboardModelState(newProps);
    }
  }

  updateDashboardModelState(newProps) {
    if (this.props.dashboardModel && !this.modelCreated) {
      this.setState(prepareDashboardModelForState(newProps.dashboardModel, newProps.lists, newProps.fields));
      this.modelCreated = true;
    } else {
      this.setState({
        fields: newProps.fields
      });
    }
  }

  getSelectedFields(allFields, selectedFields) {
    return _.filter(selectedFields, cf => {
      return _.findIndex(allFields, { extId: cf }) > -1;
    });
  }

  createDashboardModel() {
    return {
      boardId: this.state.boardId,
      board: {
        name: this.state.name,
        _id: this.state.boardId
      },
      name: this.state.name,
      todo: this.state.todo.map(i => i._id),
      wip: this.state.wip.map(i => i._id),
      done: this.state.done.map(i => i._id),
      selectedFields: this.state.selectedFields,
      cumulative: this.state.cumulative,
      isAzure: this.state.isAzure,
      isCsv: this.state.isCsv,
      wipeOut: this.state.wipeOut
    };
  }

  handleCreate() {
    if (this.props.saving) {
      return false;
    }

    if (!this.state.name) {
      this.props.setError({
        data: {
          dashboard: {
            msg: 'error.dashboard.name'
          }
        }
      });
      return false;
    }

    const model = this.createDashboardModel();
    model.anonymize = this.state.anonymize;

    this.props.create(model);
  }

  handleUpdate() {
    if (this.props.saving) {
      return false;
    }

    if (!this.state.name) {
      this.props.setError({
        data: {
          dashboard: {
            msg: 'error.dashboard.name'
          }
        }
      });
      return false;
    }

    const model = this.createDashboardModel();
    this.props.update(this.props.dashboardModel._id, model);
  }

  handleCheckboxChange(e) {
    this.setState({ wipeOut: e.checked });
  }

  _logFileMeta = file => {
    this.props.setError(
      `Detected file: ${JSON.stringify({
        name: file.name,
        type: file.type,
        size: file.size
      })}`,
      { show: false }
    );
  };

  _clearLoading() {
    this.setState({
      uploading: false
    });
    return false;
  }

  _uploadFile(ev, file) {
    if (file.size && file.size > this.state.maxSize) {
      this.props.setError('File must be less than 32MB');
      return this._clearLoading();
    }

    let isAzure = false;
    let isCsv = file.type.indexOf('csv') > -1 || file.type.indexOf('excel') > -1;
    if (!isCsv) {
      isCsv = file.name.indexOf('.csv') > -1;
    }
    if (!isCsv) {
      isCsv = file.name.indexOf('.json') === -1;
    }

    if (!isCsv) {
      let json;
      try {
        json = JSON.parse(ev.target.result);
      } catch (err) {
        this._logFileMeta(file);
        this.props.setError(err);
        return this._clearLoading();
      }

      isAzure = _.isArray(json) && _.every(json, j => _.isObject(j.task) && _.isArray(j.history));

      if (!isAzure && (!json.issues || !_.isArray(json.issues))) {
        this.props.setError({
          data: {
            file: {
              msg: 'error.file.invalid'
            }
          }
        });
        return this._clearLoading();
      }
    }

    const type = isCsv ? 'csv' : isAzure ? 'azure' : 'jira';
    this.props.uploadFile(this.state.boardId, file, isCsv, type, this.state.dashboardId).then(
      res => {
        if (res.mapping && !this.state.todo.length && !this.state.wip.length && !this.state.done.length) {
          this.setState({
            todo: getListsByIds(res.lists, res.mapping.todo),
            wip: getListsByIds(res.lists, res.mapping.wip),
            done: getListsByIds(res.lists, res.mapping.done)
          });
        }

        const mappedLists = _.concat(this.state.todo, this.state.wip, this.state.done);
        const lists = _.filter(res.lists, l => {
          return _.findIndex(mappedLists, { _id: l._id }) === -1;
        });

        this.setState({
          uploading: false,
          listsLoaded: true,
          boardId: res.id,
          isCsv,
          isAzure,
          lists,
          fields: res.customFields,
          selectedFields: !this.state.selectedFields.length
            ? _(res.customFields)
              .filter(cf => cf.extId !== 'assignees')
              .map(cf => cf.extId)
              .value()
            : this.state.selectedFields
        });
      },
      res => {
        this._logFileMeta(file);
        if (res && res.id) {
          this.setState({
            uploading: false,
            boardId: res.id
          });
        } else {
          return this._clearLoading();
        }
      }
    );
  }

  handleFileDrop(file) {
    if (!file.length) {
      this.props.setError({
        data: {
          file: {
            msg: 'error.file.invalid'
          }
        }
      });
      return false;
    }

    this.setState({
      uploading: true,
      listsLoaded: false
    });

    const fileReader = new FileReader();

    fileReader.onerror = err => {
      this.setError(err);
    };

    fileReader.onload = event => {
      this._uploadFile(event, file[0]);
    };

    fileReader.readAsText(file[0]);
  }

  id2List = {
    lists: 'lists',
    todo: 'todo',
    wip: 'wip',
    done: 'done'
  };

  getList = id => this.state[this.id2List[id]];

  onDragEnd = result => {
    const { source, destination } = result;

    if (!destination) {
      return;
    }

    if (source.droppableId === destination.droppableId) {
      const items = reorder(this.getList(source.droppableId), source.index, destination.index);
      this.setState({ [source.droppableId]: items });
    } else {
      const resultArrs = move(
        this.getList(source.droppableId),
        this.getList(destination.droppableId),
        source,
        destination
      );

      this.setState({
        lists: resultArrs.lists || this.state.lists,
        todo: resultArrs.todo || this.state.todo,
        wip: resultArrs.wip || this.state.wip,
        done: resultArrs.done || this.state.done
      });
    }
  };

  fieldIsSelected = id => {
    return this.state.selectedFields.indexOf(id) > -1;
  };

  selectField = field => {
    const selected = this.state.selectedFields;
    if (!this.fieldIsSelected(field.extId)) {
      selected.push(field.extId);
    } else {
      const index = selected.indexOf(field.extId);
      selected.splice(index, 1);
    }

    this.setState(
      {
        selectedFields: selected,
        fieldsValue: selected.length > 0 ? `${selected.length} field${selected.length !== 1 ? 's' : ''} selected` : ''
      },
      () => this.handleFieldsAutocompleteVisibility(true)
    );
  };

  handleFieldsAutocompleteVisibility = isOpen => {
    this.setState({
      fieldSearchVal: isOpen ? '' : this.state.fieldsValue,
      fieldsAutocompleteShouldOpen: isOpen
    });
  };

  handleOutsideClick = e => {
    const cfa = document.getElementById('fields-autocomplete');
    if (cfa && cfa.contains(e.target)) {
      return false;
    } else if (cfa) {
      const menu = cfa.querySelector('#menu');
      const className = e.target.className && typeof e.target.className === 'string' ? `.${e.target.className}` : '';
      if (menu && menu.querySelector(`${e.target.localName}${className}`)) {
        return false;
      }
    }

    this.handleFieldsAutocompleteVisibility(false);
  };

  handleDashboardNameChange = e => {
    if (!e.target.value) {
      this.props.setError({
        data: {
          dashboard: {
            msg: 'error.dashboard.name'
          }
        }
      });

      return false;
    }

    if (!!this.props.dashboardModel && e.target.value !== this.props.dashboardModel.name) {
      this.props.updateDashboardName(this.props.params.id, e.target.value);
    }
  };

  renderDroppable = (name, title, text, list, className) => {
    return [
      <div key={name} className={className}>
        <h4 className='title_label'>{title} ({list.length})</h4>
        <Droppable droppableId={name}>
          {(provided, snapshot) => (
            <div ref={provided.innerRef} style={getListStyle(snapshot.isDraggingOver)} {...provided.droppableProps}>
              {list.map((item, index) => (
                <Draggable key={item._id} draggableId={item._id} index={index}>
                  {(provided, snapshot) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      {...provided.dragHandleProps}
                      style={getItemStyle(snapshot.isDragging, provided.draggableProps.style)}
                    >
                      {item.name}
                    </div>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
              <div className='drag_drop_box'>{text}</div>
              {(list.length == 0) &&  
                <React.Fragment>
                  {name == 'lists' && 
                    <div className="well_done_box">
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="25" viewBox="0 0 24 25" fill="none">
                        <path fill-rule="evenodd" clip-rule="evenodd" d="M2.00574 12.873C2.00574 7.35305 6.48574 2.87305 12.0057 2.87305C17.5257 2.87305 22.0057 7.35305 22.0057 12.873C22.0057 18.393 17.5257 22.873 12.0057 22.873C6.48574 22.873 2.00574 18.393 2.00574 12.873ZM5.70574 13.573L9.29574 17.163C9.68574 17.553 10.3257 17.553 10.7057 17.163L18.2957 9.57305C18.6857 9.18305 18.6857 8.55305 18.2957 8.16305C17.9057 7.77305 17.2757 7.77305 16.8857 8.16305L10.0057 15.043L7.11574 12.163C6.72574 11.773 6.09574 11.773 5.70574 12.163C5.51848 12.3499 5.41325 12.6035 5.41325 12.868C5.41325 13.1326 5.51848 13.3862 5.70574 13.573Z" fill="#0E391F"/>
                      </svg>
                      <span>Well done! All your statuses are mapped. Your analytics are now set up for accurate insights.</span>
                    </div>
                  }
                </React.Fragment>
              }
              
              {name == 'lists' && list.length > 0 && 
                <div className="time_spent_box">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="25" viewBox="0 0 24 25" fill="none">
                    <path fill-rule="evenodd" clip-rule="evenodd" d="M19.5276 21.2795C21.0676 21.2795 22.0276 19.6095 21.2576 18.2795L13.7276 5.26947C12.9576 3.93947 11.0376 3.93947 10.2676 5.26947L2.73756 18.2795C1.96756 19.6095 2.92756 21.2795 4.46756 21.2795H19.5276ZM11.9976 14.2795C11.4476 14.2795 10.9976 13.8295 10.9976 13.2795V11.2795C10.9976 10.7295 11.4476 10.2795 11.9976 10.2795C12.5476 10.2795 12.9976 10.7295 12.9976 11.2795V13.2795C12.9976 13.8295 12.5476 14.2795 11.9976 14.2795ZM10.9976 16.2795V18.2795H12.9976V16.2795H10.9976Z" fill="#B37100"/>
                  </svg>
                  <span>Time spent in unmapped statuses won’t be tracked in your analytics. Only leave statuses unmapped if they’re no longer in use.</span>
                </div>
              }
            </div>
          )}
        </Droppable>
      </div>
    ];
  };

  renderLists = () => {
    return (
      <DragDropContext onDragEnd={this.onDragEnd}>
        {this.renderDroppable(
          'lists',
          <span>
            Unmapped <FormattedMessage id={`${this.props.app}.list`} mode="capitalize" plural="true" />
          </span>,
          <span>Drag & drop <FormattedMessage id={`${this.props.app}.list`} plural="true" /> you wish to unmap here</span>,
          this.state.lists,
          'trello_select_process lists'
        )}
        {this.renderDroppable(
          'todo',
          'To Do',
          <span>
            Drag & drop your <FormattedMessage id={`${this.props.app}.list`} plural="true" /> with tasks that haven't started yet here
          </span>,
          this.state.todo,
          'trello_list_process todo'
        )}
        {this.renderDroppable(
          'wip',
          'Doing',
          <span>
            Drag & drop your <FormattedMessage id={`${this.props.app}.list`} plural="true" /> with tasks in progress here
          </span>,
          this.state.wip,
          'trello_list_process doing'
        )}
        {this.renderDroppable(
          'done',
          'Done',
          <span>
            Drag & drop your <FormattedMessage id={`${this.props.app}.list`} plural="true" /> with completed tasks here
          </span>,
          this.state.done,
          'trello_list_process done'
        )}
      </DragDropContext>
    );
  };

  trackHowToLoadOpen() {
    trackHowToOpened('load');
  }

  render() {
    const { uploading, fieldSearchVal, listsLoaded, todo, wip, done } = this.state;
    const { app, appName, dashboardModel } = this.props;

    return [
      <Header key="1" title="" />,
      <React.Fragment>
        <div key="2" id="create-dashboard" className='bg_dashboard file'>
          <Helmet title={dashboardModel ? 'Update dashboard' : 'Create dashboard'} />
          <div className={`action_button ${(!dashboardModel && listsLoaded && !this.state.isAzure && !this.state.isCsv) ? 'inline-checkbox' : ''}`} >
          {listsLoaded && !this.state.isAzure && !this.state.isCsv && (
              <Switch
                name="anonymize"
                id="anonymize"
                label="Don't store task names"
                disabled={!!dashboardModel || !(todo.length && wip.length && done.length)}
                checked={this.state.anonymize}
                onChange={e => this.setState({ anonymize: e.checked })}
              />
            )}

            {dashboardModel ? (
              [
                hasDashboardAccess(this.props.user, dashboardModel._id, 'edit') ? (
                  <Switch
                    name="wipeOut"
                    id="wipeOut"
                    key="4"
                    label="Wipe out dashboard data"
                    checked={this.state.wipeOut}
                    onChange={this.handleCheckboxChange}
                    disabled={!(todo.length && wip.length && done.length)}
                  />
                ) : (
                  undefined
                ),
                <Confirm
                  key="3"
                  disabled={!this.state.wipeOut}
                  onConfirm={ev => this.handleUpdate()}
                  body={
                    <p>
                      We'll remove all your dashboard historical 
                      records and we'll sync your data again. We'll preserve the configuration of your
                      dashboard.
                    </p>
                  }
                  confirmText="Confirm"
                  icon={deleteIcon}
                  title="Wipe out dashboard data"
                >
                  <Button
                    className='btn-mw-200'
                    disabled={!(todo.length && wip.length && done.length)}
                    onClick={this.handleUpdate}
                  >
                    <img src={saveIcon} alt="" />
                    {this.props.saving ? 'Loading...' : 'Update dashboard'}
                  </Button>
                </Confirm>
              ]
            ) : (
              <Button
                className='btn-mw-200'
                disabled={!(todo.length && wip.length && done.length)}
                onClick={this.handleCreate}
              >
                <img src={addIcon} alt="" />
                {this.props.saving ? 'Loading...' : 'Create dashboard'}
              </Button>
            )}
            
          </div>

          <div className='head_control'>
            <div className='dashboard_name'>
              <p className='title_label fw_sb'>Dashboard name</p>
              <input
                placeholder="Dashboard name"
                value={this.state.name}
                onChange={e => this.setState({ name: e.target.value })}
                onBlur={this.handleDashboardNameChange}></input>
            </div>
            
            {this.state.fields && !!this.state.fields.length && (
              <div className='autocomplete'>
                <p className='title_label fw_sb'>Enable custom fields</p>
                <Autocomplete
                  wrapperProps={{ id: 'fields-autocomplete' }}
                  ref={el => (this.cfInput = el)}
                  getItemValue={item => item.name}
                  items={this.state.fields}
                  renderInput={props => <input placeholder="Search" {...props} />}
                  renderMenu={(items, value, style) => {
                    return items.length ? (
                      <div id="menu" style={{ ...style, ...this.menuStyle }} children={items} />
                    ) : (
                      <div className='no_boards_found'>
                        <span>No custom fields found</span>
                      </div>
                    );
                  }}
                  renderItem={(field, isHighlighted) => (
                    <div
                      key={field.extId}
                      className='dropdown_list'
                      style={{ background: isHighlighted ? '#E6F1F7' : '' }}
                    >
                      <Checkbox
                        name={field.extId}
                        id={field.extId}
                        label={field.name}
                        checked={this.fieldIsSelected(field.extId)}
                        onChange={e => this.selectField(field)}
                      />
                    </div>
                  )}
                  value={fieldSearchVal}
                  onChange={(ev, value) => this.setState({ fieldSearchVal: value })}
                  onSelect={(value, field) => this.selectField(field)}
                  shouldItemRender={(field, value) => {
                    return field.name.toLowerCase().indexOf(value.toLowerCase()) !== -1;
                  }}
                  open={this.state.fieldsAutocompleteShouldOpen}
                  onMenuVisibilityChange={this.handleFieldsAutocompleteVisibility}
                />
              </div>
            )}
          </div>

          {!dashboardModel && (
            <div className='help'>
              <span>
                Please follow our guidelines on{' '}
                <a
                  target="_blank"
                  href="https://getnave.com/blog/loading-data-to-nave"
                  onClick={this.trackHowToLoadOpen}
                  onAuxClick={this.trackHowToLoadOpen}
                >
                  how to load your data to Nave.
                </a>
              </span>
            </div>
          )}

          {(!!dashboardModel || !listsLoaded) && <DropZone onDrop={files => this.handleFileDrop(files)} />}

          {listsLoaded && (
            <div className='lists_section'>
              {dashboardModel && (
                  <div className='check-wip-data'>
                    <Checkbox
                      name={'cumulative'}
                      id={'cumulative'}
                      label={'Accumulate WIP data'}
                      checked={this.state.cumulative}
                      onChange={e => this.setState({ cumulative: e.checked })}
                    />
                  </div>
              )}
              <p className='title_label fw_sb'>
                Map <FormattedMessage id={`${app}.list`} prefix={appName} mode="capitalize" plural="true" />
              </p>
              <div className="list_arrow">
                <img src={spinerArrow} alt="#" />
              </div>
              <div className='lists_section_scroll'>
                {this.renderLists()}
              </div>
            </div>
          )}
        </div>
        {!!uploading && <div className='table-loading'><Loading /></div>}
      </React.Fragment>
    ];
  }
}

const mapDispatchToProps = dispatch => {
  return {
    setError: (err, opt) => dispatch(handleError(err, opt)),
    setSuccess: msg => dispatch(setSuccessMessage(msg)),
    setDataLoading: val => dispatch(appDataIsLoading(val)),
    loadingDone: () => dispatch(isLoading(false)),
    listAll: () => dispatch(listAll()),
    getLists: (boardId, isAzure) => dispatch(getLists(boardId, undefined, undefined, isAzure)),
    create: model => dispatch(create(model)),
    update: (boardId, model) => dispatch(update(boardId, model)),
    updateModel: dashboard => dispatch(updateModel(dashboard)),
    updateDashboardName: (dashboardId, name) => dispatch(updateDashboardName(dashboardId, name)),
    uploadFile: (id, file, isCsv, type, dashboardId) => dispatch(uploadFile(id, file, isCsv, type, dashboardId)),
    resetModel: () => {
      dispatch(updateModel(undefined));
    }
  };
};

const mapStateToProps = state => {
  return {
    app: state.app.app,
    appName: state.app.appName,
    user: state.auth.user,
    dashboards: state.dashboards.list,
    lists: state.dashboards.lists,
    fields: state.dashboards.fields,
    created: state.dashboards.newCreated,
    dashboardModel: state.dashboards.dashboardModel,
    saving: state.dashboards.isLoading
  };
};

export { CreateFileDashboard };
export default withRouter(connect(mapStateToProps, mapDispatchToProps)(CreateFileDashboard));
